import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js';
import { decodeJwt } from 'jose';
import { useEffect, useState, useSyncExternalStore } from 'react';

export interface PublicConfig { supabaseUrl: string; supabasePublishableKey: string; country: string; emailEnabled: boolean }
let configPromise: Promise<PublicConfig> | undefined;
let clientPromise: Promise<SupabaseClient> | undefined;
export type AuthCallback = { status: 'none' | 'pending' | 'complete' | 'recovery' } | { status: 'error'; message: string };
let authCallback: AuthCallback = { status: 'none' };
const callbackListeners = new Set<() => void>();
function setAuthCallback(value: AuthCallback) {
  authCallback = value;
  for (const listener of callbackListeners) listener();
}
function subscribeAuthCallback(listener: () => void) {
  callbackListeners.add(listener);
  return () => { callbackListeners.delete(listener); };
}
let recoverySession: string | null = null;
const recoveryLinkError = 'This reset link could not be verified. Request a new link, then open it in the same browser and device where you requested it.';

function sessionIdentity(session: Session | null): string | null {
  if (!session) return null;
  try {
    // Only compare sessions already accepted by Supabase; this is not JWT authorization.
    const sessionId = decodeJwt(session.access_token).session_id;
    return typeof sessionId === 'string' && sessionId ? `${session.user.id}:${sessionId}` : null;
  } catch { return null; }
}

async function handleAuthCallback(client: SupabaseClient) {
  const url = new URL(location.href);
  const hash = new URLSearchParams(url.hash.slice(1));
  const wantsRecovery = url.searchParams.get('account') === 'recovery';
  const code = url.searchParams.get('code');
  const hasError = ['error', 'error_code', 'error_description'].some(key => url.searchParams.has(key) || hash.has(key));
  if (!code && !hasError && !wantsRecovery) return;
  setAuthCallback({ status: 'pending' });
  let verifiedRecovery: string | null = null;
  // Register before exchanging: the SDK emits PASSWORD_RECOVERY from the stored
  // PKCE verifier, not from the untrusted account/type URL parameters.
  const { data: listener } = client.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') verifiedRecovery = sessionIdentity(session);
  });
  try {
    if (hasError || !code) throw new Error('Invalid callback');
    const result = await client.auth.exchangeCodeForSession(code);
    if (result.error || !result.data.session) throw result.error ?? new Error('Missing callback session');
    if (verifiedRecovery && verifiedRecovery === sessionIdentity(result.data.session)) {
      recoverySession = verifiedRecovery;
      setAuthCallback({ status: 'recovery' });
    } else if (wantsRecovery) {
      throw new Error('This callback was not password recovery');
    } else {
      setAuthCallback({ status: 'complete' });
    }
  } catch {
    recoverySession = null;
    setAuthCallback({ status: 'error', message: wantsRecovery ? recoveryLinkError : 'This sign-in link could not be verified. Request a new link and open it in the same browser and device.' });
  } finally {
    listener.subscription.unsubscribe();
    // One-use codes and provider error details should not linger in history.
    for (const key of ['code', 'sb_flow_id', 'error', 'error_code', 'error_description']) url.searchParams.delete(key);
    if (hasError && ['error', 'error_code', 'error_description'].some(key => hash.has(key))) url.hash = '';
    history.replaceState(history.state, '', url.toString());
  }
}

export function getConfig(): Promise<PublicConfig> {
  configPromise ??= fetch('/api/v1/config').then(async r => {
    if (!r.ok) throw new Error('Account services could not be reached. Please try again.');
    return r.json() as Promise<PublicConfig>;
  }).catch(error => { configPromise = undefined; throw error; });
  return configPromise;
}
export function getSupabase(): Promise<SupabaseClient> {
  clientPromise ??= getConfig().then(async config => {
    if (!config.supabaseUrl || !config.supabasePublishableKey) throw new Error('Account services are not available yet.');
    const client = createClient(config.supabaseUrl, config.supabasePublishableKey, {
      auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, storageKey: 'dairyoak.auth' },
    });
    await handleAuthCallback(client);
    return client;
  }).catch(error => {
    clientPromise = undefined;
    const url = new URL(location.href);
    const hash = new URLSearchParams(url.hash.slice(1));
    if (url.searchParams.has('code') || url.searchParams.get('account') === 'recovery' || ['error', 'error_code', 'error_description'].some(key => url.searchParams.has(key) || hash.has(key)) || authCallback.status === 'pending') {
      setAuthCallback({ status: 'error', message: 'Account services could not be reached. Reload this page to try your link again.' });
    }
    throw error;
  });
  return clientPromise;
}
export class ApiError extends Error {
  constructor(message: string, public status: number, public code: string, public details?: unknown) { super(message); this.name = 'ApiError'; }
}
export type ApiOptions = Omit<RequestInit, 'body'> & { body?: unknown; public?: boolean };
export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const requestPath = path.startsWith('/api/') ? path : `/api/v1${path.startsWith('/') ? path : `/${path}`}`;
  const publicPath = options.public || /^\/api\/v1\/(?:properties|sources|locations|weather|catalog)(?:\/|\?|$)/.test(requestPath);
  if (publicPath) {
    // Public browsing must not wait for account configuration or session refresh.
    headers.delete('Authorization');
  } else {
    const session = (await (await getSupabase()).auth.getSession()).data.session;
    if (session) headers.set('Authorization', `Bearer ${session.access_token}`);
  }
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  const response = await fetch(requestPath, {
    ...options, headers, credentials: 'same-origin', body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json();
  if (!response.ok) {
    const value = body as { error?: { message?: string; code?: string; details?: unknown } | string; message?: string; code?: string };
    const error = typeof value.error === 'object' ? value.error : undefined;
    throw new ApiError(error?.message || value.message || (typeof value.error === 'string' ? value.error : 'Your request could not be completed.'), response.status, error?.code || value.code || 'request_failed', error?.details);
  }
  return body as T;
}
export async function signIn(email: string, password: string) {
  const result = await (await getSupabase()).auth.signInWithPassword({ email, password });
  if (result.error) throw result.error;
  return result.data;
}
export async function signUp(email: string, password: string, displayName: string) {
  if (!(await getConfig()).emailEnabled) throw new Error('Account email is being configured. You can draw on this device in the meantime.');
  const result = await (await getSupabase()).auth.signUp({ email, password, options: { emailRedirectTo: `${location.origin}/`, data: { display_name: displayName } } });
  if (result.error) throw result.error;
  return result.data;
}
export async function sendMagicLink(email: string) {
  if (!(await getConfig()).emailEnabled) throw new Error('Account email is not available yet. Use your password to sign in.');
  const result = await (await getSupabase()).auth.signInWithOtp({ email, options: { emailRedirectTo: `${location.origin}/` } });
  if (result.error) throw result.error;
}
export async function resetPassword(email: string) {
  if (!(await getConfig()).emailEnabled) throw new Error('Password recovery email is not available yet. Please try again once account email is enabled.');
  const result = await (await getSupabase()).auth.resetPasswordForEmail(email, { redirectTo: `${location.origin}/?account=recovery` });
  if (result.error) throw result.error;
}
export async function completePasswordRecovery(password: string) {
  const client = await getSupabase();
  const current = await client.auth.getSession();
  if (authCallback.status !== 'recovery' || !recoverySession || current.error || sessionIdentity(current.data.session) !== recoverySession) {
    recoverySession = null;
    setAuthCallback({ status: 'error', message: recoveryLinkError });
    throw new Error(recoveryLinkError);
  }
  const result = await client.auth.updateUser({ password });
  if (result.error) throw result.error;
  recoverySession = null;
  setAuthCallback({ status: 'complete' });
  const url = new URL(location.href);
  url.searchParams.delete('account');
  history.replaceState(history.state, '', url.toString());
}
export async function signOut() {
  const result = await (await getSupabase()).auth.signOut({ scope: 'local' });
  if (result.error) throw result.error;
}
export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const callback = useSyncExternalStore(subscribeAuthCallback, () => authCallback);
  useEffect(() => {
    let active = true;
    let cleanup: (() => void) | undefined;
    void getSupabase().then(async client => {
      const { data } = client.auth.onAuthStateChange((_event, current) => { if (active) { setSession(current); setLoading(false); } });
      cleanup = () => data.subscription.unsubscribe();
      if (!active) { cleanup(); return; }
      const result = await client.auth.getSession();
      if (result.error) throw result.error;
      if (active) { setSession(result.data.session); setLoading(false); }
    }).catch(reason => { if (active) { setError(reason instanceof Error ? reason.message : 'Account services unavailable.'); setLoading(false); } });
    return () => { active = false; cleanup?.(); };
  }, []);
  return { session, user: session?.user ?? null, loading, error, callback, signOut };
}
export const useAuth = useSession;
